import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { CognitoUserPool, CognitoUser, AuthenticationDetails } from 'amazon-cognito-identity-js';
import awsConfig from '../config/aws-config';
import {
  getAccessDeniedMessage,
  storeAccessDeniedMessage,
} from '../config/accessControl';
import {
  setAuthToken,
  setupTokenRefresh,
  setupAccessDeniedHandler,
  getSubscriptionStatus,
  isAccessDeniedError,
} from '../services/api';

const AuthContext = createContext();

const userPool = new CognitoUserPool({
  UserPoolId: awsConfig.userPoolId,
  ClientId: awsConfig.clientId
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [idToken, setIdToken] = useState(null);

  const clearSession = useCallback(() => {
    const cognitoUser = userPool.getCurrentUser();
    if (cognitoUser) {
      cognitoUser.signOut();
    }
    setUser(null);
    setIdToken(null);
    setAuthToken(null);
  }, []);

  const handleAccessDenied = useCallback((message) => {
    storeAccessDeniedMessage(message || getAccessDeniedMessage());
    clearSession();
  }, [clearSession]);

  const verifyApiAccess = useCallback(async () => {
    try {
      await getSubscriptionStatus();
      return true;
    } catch (error) {
      if (isAccessDeniedError(error)) {
        return false;
      }
      // Allow through on transient/network errors — do not lock out on API blips.
      return true;
    }
  }, []);

  const refreshToken = useCallback(() => {
    return new Promise((resolve, reject) => {
      const cognitoUser = userPool.getCurrentUser();

      if (!cognitoUser) {
        reject(new Error('No user found'));
        return;
      }

      cognitoUser.getSession((err, session) => {
        if (err) {
          reject(err);
          return;
        }

        const newToken = session.getIdToken().getJwtToken();
        setIdToken(newToken);
        resolve(newToken);
      });
    });
  }, []);

  const checkUser = useCallback(() => {
    const cognitoUser = userPool.getCurrentUser();
    if (!cognitoUser) {
      setLoading(false);
      return;
    }

    cognitoUser.getSession((err, session) => {
      if (err) {
        setLoading(false);
        return;
      }
      if (!session.isValid()) {
        setLoading(false);
        return;
      }

      cognitoUser.getUserAttributes(async (attrErr, attributes) => {
        if (attrErr) {
          setLoading(false);
          return;
        }

        const token = session.getIdToken().getJwtToken();
        setAuthToken(token);

        const allowed = await verifyApiAccess();
        if (!allowed) {
          handleAccessDenied();
          setLoading(false);
          return;
        }

        const userData = attributes.reduce((acc, attribute) => {
          acc[attribute.Name] = attribute.Value;
          return acc;
        }, {});
        setUser({ ...userData, username: cognitoUser.getUsername() });
        setIdToken(token);
        setLoading(false);
      });
    });
  }, [verifyApiAccess, handleAccessDenied]);

  useEffect(() => {
    checkUser();
    setupTokenRefresh(refreshToken);
    setupAccessDeniedHandler(handleAccessDenied);
  }, [checkUser, handleAccessDenied, refreshToken]);

  const signUp = (email, password, attributes) => {
    return new Promise((resolve, reject) => {
      const attributeList = Object.keys(attributes).map(key => ({
        Name: key,
        Value: attributes[key]
      }));

      userPool.signUp(email, password, attributeList, null, (err, result) => {
        if (err) {
          reject(err);
          return;
        }
        resolve(result);
      });
    });
  };

  const confirmSignUp = (username, code) => {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: username,
        Pool: userPool
      });

      cognitoUser.confirmRegistration(code, true, (err, result) => {
        if (err) {
          reject(err);
          return;
        }
        resolve(result);
      });
    });
  };

  const signIn = (email, password) => {
    return new Promise((resolve, reject) => {
      const authenticationDetails = new AuthenticationDetails({
        Username: email,
        Password: password
      });

      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: userPool
      });

      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: (session) => {
          cognitoUser.getUserAttributes(async (err, attributes) => {
            if (err) {
              reject(err);
              return;
            }

            const token = session.getIdToken().getJwtToken();
            setAuthToken(token);

            const allowed = await verifyApiAccess();
            if (!allowed) {
              handleAccessDenied();
              reject(new Error(getAccessDeniedMessage()));
              return;
            }

            const userData = attributes.reduce((acc, attribute) => {
              acc[attribute.Name] = attribute.Value;
              return acc;
            }, {});
            setUser({ ...userData, username: cognitoUser.getUsername() });
            setIdToken(token);
            resolve(session);
          });
        },
        onFailure: (err) => {
          reject(err);
        }
      });
    });
  };

  const signOut = () => {
    clearSession();
  };

  const getIdToken = () => {
    return new Promise((resolve, reject) => {
      const cognitoUser = userPool.getCurrentUser();
      if (!cognitoUser) {
        reject(new Error('No user found'));
        return;
      }

      cognitoUser.getSession((err, session) => {
        if (err) {
          reject(err);
          return;
        }
        resolve(session.getIdToken().getJwtToken());
      });
    });
  };

  const forgotPassword = (email) => {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: userPool
      });

      cognitoUser.forgotPassword({
        onSuccess: (result) => {
          resolve(result);
        },
        onFailure: (err) => {
          reject(err);
        }
      });
    });
  };

  const resetPassword = (email, code, newPassword) => {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: userPool
      });

      cognitoUser.confirmPassword(code, newPassword, {
        onSuccess: () => {
          resolve();
        },
        onFailure: (err) => {
          reject(err);
        }
      });
    });
  };

  const changePassword = (oldPassword, newPassword) => {
    return new Promise((resolve, reject) => {
      const cognitoUser = userPool.getCurrentUser();
      if (!cognitoUser) {
        reject(new Error('No user found'));
        return;
      }

      cognitoUser.getSession((err) => {
        if (err) {
          reject(err);
          return;
        }

        cognitoUser.changePassword(oldPassword, newPassword, (changeErr, result) => {
          if (changeErr) {
            reject(changeErr);
            return;
          }
          resolve(result);
        });
      });
    });
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      idToken,
      signUp,
      confirmSignUp,
      signIn,
      signOut,
      getIdToken,
      forgotPassword,
      resetPassword,
      changePassword
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export default AuthContext;
